import { call, put, select, takeLatest } from 'redux-saga/effects';
import i18n from '../../../i18n';
import {
  fetchFirmwareVersions,
  fetchFirmwareVersionsSuccess,
  fetchFirmwareVersionsFailure,
  fetchFirmwareVersionById,
  fetchFirmwareVersionByIdSuccess,
  fetchFirmwareVersionByIdFailure,
  addFirmwareVersion,
  addFirmwareVersionSuccess,
  addFirmwareVersionFailure,
  updateFirmwareVersion,
  updateFirmwareVersionSuccess,
  updateFirmwareVersionFailure,
  deleteFirmwareVersion,
  deleteFirmwareVersionSuccess,
  deleteFirmwareVersionFailure,
  bulkDeleteFirmwareVersions,
  bulkDeleteFirmwareVersionsSuccess,
  bulkDeleteFirmwareVersionsFailure
} from './slice';
import * as firmwareVersionService from '../../../services/firmwareVersionService';

// Selector to get current state
const getFirmwareVersionsState = (state) => state.firmwareVersions;

// Fetch firmware versions saga
function* fetchFirmwareVersionsSaga(action) {
  try {
    const payload = action.payload || {};

    // Map frontend parameter names to backend DTO property names
    const params = {
      page: payload.page,
      pageSize: payload.pageSize,
      sortColumn: payload.sortColumn,
      sortDirection: payload.sortDirection,
    };

    // Only add searchTerm if it has a value
    if (payload.filterVersion && payload.filterVersion.trim() !== '') {
      params.searchTerm = payload.filterVersion;
      params.search = payload.filterVersion; // align with Vehicles naming
    }

    // Only add deviceType if it has a value
    if (payload.filterDeviceType && payload.filterDeviceType !== '') {
      params.deviceType = parseInt(payload.filterDeviceType, 10);
    }

    const response = yield call(firmwareVersionService.getAllFirmwareVersions, params);
    yield put(fetchFirmwareVersionsSuccess(response));
  } catch (error) {
    // Extract error details including traceId
    const errorData = error.response?.data || error;
    const errorMessage = errorData?.message || error.message;
    const traceId = errorData?.traceId;

    // Log error to console with traceId for debugging
    if (traceId) {
      console.error(`[TraceId: ${traceId}] Failed to fetch firmware versions:`, {
        message: errorMessage,
        traceId,
        requestData: action.payload,
        fullError: errorData
      });
    } else {
      console.error('Failed to fetch firmware versions:', {
        message: errorMessage,
        requestData: action.payload,
        fullError: errorData
      });
    }

    yield put(fetchFirmwareVersionsFailure(errorMessage));
  }
}

// Fetch firmware version by ID saga
function* fetchFirmwareVersionByIdSaga(action) {
  try {
    const response = yield call(firmwareVersionService.getFirmwareVersionById, action.payload);
    yield put(fetchFirmwareVersionByIdSuccess(response));
  } catch (error) {
    // Extract error details including traceId
    const errorData = error.response?.data || error;
    const errorMessage = errorData?.message || error.message;
    const traceId = errorData?.traceId;

    // Log error to console with traceId for debugging
    if (traceId) {
      console.error(`[TraceId: ${traceId}] Failed to fetch firmware version by ID:`, {
        message: errorMessage,
        traceId,
        id: action.payload,
        fullError: errorData
      });
    } else {
      console.error('Failed to fetch firmware version by ID:', {
        message: errorMessage,
        id: action.payload,
        fullError: errorData
      });
    }

    yield put(fetchFirmwareVersionByIdFailure(errorMessage));
  }
}

// Add firmware version saga
function* addFirmwareVersionSaga(action) {
  try {
    const response = yield call(firmwareVersionService.createFirmwareVersion, action.payload);
    yield put(addFirmwareVersionSuccess({
      data: response,
      message: response.message
    }));
  } catch (error) {
    // Extract error details including traceId
    const errorData = error.response?.data || error;
    const errorMessage = errorData?.message || error.message;
    const traceId = errorData?.traceId;

    // Log error to console with traceId for debugging
    if (traceId) {
      console.error(`[TraceId: ${traceId}] Failed to add firmware version:`, {
        message: errorMessage,
        traceId,
        requestData: action.payload,
        fullError: errorData
      });
    } else {
      console.error('Failed to add firmware version:', {
        message: errorMessage,
        requestData: action.payload,
        fullError: errorData
      });
    }

    yield put(addFirmwareVersionFailure(errorMessage));
  }
}

// Update firmware version saga
function* updateFirmwareVersionSaga(action) {
  try {
    const { id, ...data } = action.payload;

    const response = yield call(firmwareVersionService.updateFirmwareVersion, id, data);
    yield put(updateFirmwareVersionSuccess({
      data: response,
      message: response.message
    }));
  } catch (error) {
    // Extract error details including traceId
    const errorData = error.response?.data || error;
    const errorMessage = errorData?.message || error.message;
    const traceId = errorData?.traceId;

    // Log error to console with traceId for debugging
    if (traceId) {
      console.error(`[TraceId: ${traceId}] Failed to update firmware version:`, {
        message: errorMessage,
        traceId,
        requestData: action.payload,
        fullError: errorData
      });
    } else {
      console.error('Failed to update firmware version:', {
        message: errorMessage,
        requestData: action.payload,
        fullError: errorData
      });
    }

    yield put(updateFirmwareVersionFailure(errorMessage));
  }
}

// Delete firmware version saga
function* deleteFirmwareVersionSaga(action) {
  try {
    const response = yield call(firmwareVersionService.deleteFirmwareVersion, action.payload);
    yield put(deleteFirmwareVersionSuccess({
      message: response?.message
    }));
  } catch (error) {
    // Extract error details including traceId
    const errorData = error.response?.data || error;
    const errorMessage = errorData?.message || error.message;
    const traceId = errorData?.traceId;

    // Log error to console with traceId for debugging
    if (traceId) {
      console.error(`[TraceId: ${traceId}] Failed to delete firmware version:`, {
        message: errorMessage,
        traceId,
        id: action.payload,
        fullError: errorData
      });
    } else {
      console.error('Failed to delete firmware version:', {
        message: errorMessage,
        id: action.payload,
        fullError: errorData
      });
    }

    yield put(deleteFirmwareVersionFailure(errorMessage));
  }
}

// Bulk delete firmware versions saga
function* bulkDeleteFirmwareVersionsSaga(action) {
  try {
    const response = yield call(firmwareVersionService.bulkDeleteFirmwareVersions, action.payload);
    yield put(bulkDeleteFirmwareVersionsSuccess({
      message: response?.message
    }));
  } catch (error) {
    // Extract error details including traceId
    const errorData = error.response?.data || error;
    const errorMessage = errorData?.message || error.message;
    const traceId = errorData?.traceId;

    // Log error to console with traceId for debugging
    if (traceId) {
      console.error(`[TraceId: ${traceId}] Failed to bulk delete firmware versions:`, {
        message: errorMessage,
        traceId,
        ids: action.payload,
        fullError: errorData
      });
    } else {
      console.error('Failed to bulk delete firmware versions:', {
        message: errorMessage,
        ids: action.payload,
        fullError: errorData
      });
    }

    yield put(bulkDeleteFirmwareVersionsFailure(errorMessage));
  }
}

// Watcher saga
export default function* firmwareVersionsSaga() {
  yield takeLatest(fetchFirmwareVersions.type, fetchFirmwareVersionsSaga);
  yield takeLatest(fetchFirmwareVersionById.type, fetchFirmwareVersionByIdSaga);
  yield takeLatest(addFirmwareVersion.type, addFirmwareVersionSaga);
  yield takeLatest(updateFirmwareVersion.type, updateFirmwareVersionSaga);
  yield takeLatest(deleteFirmwareVersion.type, deleteFirmwareVersionSaga);
  yield takeLatest(bulkDeleteFirmwareVersions.type, bulkDeleteFirmwareVersionsSaga);
}
